import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { PersonalSettingsForm } from "@/types/my-account"
import { MyAccountSchema } from "@/constants/my-account/my-account"
import { Label } from "@/components/ui/label"
import { TextInputField } from "@/components/custom/InputField/TextInputField"
import { timezones } from '@/constants/auth/timezone'
import { Button } from "@/components/ui/button"
import { useSelector, useDispatch } from "react-redux"
import { AppDispatch, RootState } from "@/redux"
import { updatePersonalDetails } from "@/services/userService"
import { Form } from "@/components/ui/form"
import { SelectInputField } from "@/components/custom/InputField/SelectInputField"
import { getFormProps } from "@/helpers/formHelpers"

export const PersonalSettings = () => {
  const dispatch: AppDispatch = useDispatch()
  const { userData, token } = useSelector((state: RootState) => state.user)
  const form = useForm<PersonalSettingsForm>({
    resolver: zodResolver(MyAccountSchema.personalSettings),
    defaultValues: {
      firstName: userData?.firstName ?? "",
      lastName: userData?.lastName ?? "",
      timezone: userData?.timezone ?? ""
    }
  })
  const formProps = getFormProps(form, MyAccountSchema.personalSettings)

  // load userData to form
  useEffect(() => {
    if (userData) {
      form.setValue('firstName', userData.firstName)
      form.setValue('lastName', userData.lastName)
      form.setValue('timezone', userData.timezone)
    }
  }, [userData, form])

  useEffect(() => {
    form.clearErrors()
  }, [form])

  const onSubmit = async (values: PersonalSettingsForm) => {
    if (!token) return
    await dispatch(updatePersonalDetails(token, values))
  }

  const isDisabled = () => {
    return (
      form.getValues('firstName') === userData?.firstName &&
      form.getValues('lastName') === userData.lastName &&
      form.getValues('timezone') === userData.timezone
    )
  }

  return (
    <div className="py-10">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-8 flex flex-col items-start"
        >
          <Label className="font-semibold text-secondary mb-6">
            PERSONAL DETAILS
          </Label>
          <TextInputField
            name="firstName"
            label="First Name"
            placeholder="John"
            error={!!form.formState.errors.firstName}
            {...formProps}
          />
          <TextInputField
            name="lastName"
            label="Last Name"
            placeholder="Doe"
            error={!!form.formState.errors.lastName}
            {...formProps}
          />
          <SelectInputField
            name="timezone"
            label="Timezone"
            selectOptions={timezones}
            {...formProps}
          />
          <Button
            type="submit"
            className="font-semibold"
            disabled={isDisabled()}
          >
            Update Personal Details
          </Button>
        </form>
      </Form>
    </div>
  )
}
